import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Switch, FormControlLabel, Box, Typography, Button, IconButton, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import upIcon from '../assets/up_icon.png';
import downIcon from '../assets/down_icon.png';
import './../BottomSheet.css';
import { useDrag } from 'react-use-gesture';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import { apiAppFetch, mediatorAppFetch } from '../utils/apiFetch';
import { countAllVariantsForRow, formatLotCount, openedLotsForScript, pickQuantityTemplateRow } from '../utils/qtyTemplateDetail';
import { resolveFutComLotQtySteps } from '../utils/futcomLotQtyStep';
import { findWatchlistPosition, planOppositeWatchlistOrder } from '../utils/watchlistNetOrder';
import { pendingOrdersCancelledNote } from '../utils/pendingOrderNotice';

const BottomSheet = ({ userDetails, stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog, netPositions } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  // console.log("BottomSheet",stock);

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
};

  const [{ y }, api] = useSpring(() => ({ y: 0 }));

  const [successAlert, setSuccessAlert] = useState({ show: false, title: '', message: '' });

  const successAlertSpring = useSpring({
    opacity: successAlert.show ? 1 : 0,
    scale: successAlert.show ? 1 : 0.85,
    config: { tension: 300, friction: 25 }
  });

  const handleSuccessAlertClose = () => {
    setSuccessAlert({ show: false, title: '', message: '' });
    onClose();
  };

  React.useEffect(() => {
    api.start({ y: isOpen ? 0 : 100 });
    // console.log("userDetails",userDetails.FUTCOM_margin_type );
  }, [isOpen, api]);

  // const bind = useDrag(
  //   ({ down, movement: [, my], direction: [, dy], velocity }) => {
  //     if (down && dy > 0 && velocity > 0.2) {
  //       onClose();
  //     } else {
  //       api.start({ y: down ? my : 0 });
  //     }
  //   },
  //   { from: () => [0, y.get()] }
  // );

  const [isDepthExpanded, setIsDepthExpanded] = useState(true);
  const [lots, setLots] = useState(1);
  const [lotsDraft, setLotsDraft] = useState(null);
  const [quantityDraft, setQuantityDraft] = useState(null);
  const [quantity, setQuantity] = useState(Math.max(stock.lotsize || 1, 1));
  const [orderType, setOrderType] = useState('market');
  const [isLimitOrder, setIsLimitOrder] = useState(false);
  const [price, setPrice] = useState('');
  const [lotVariant, setLotVariant] = useState('MEGA'); // MEGA | MINI
  const [qtyRows, setQtyRows] = useState([]);
  const [qtyRowsSegment, setQtyRowsSegment] = useState('');
  const [qtyRowsLoading, setQtyRowsLoading] = useState(false);

  const scriptKey = String(stock.scriptName || stock.dispName || stock.dispname || '')
    .trim()
    .toUpperCase();
  const miniScripts = (userDetails && userDetails.FUTCOM_mini_scripts) || {};
  const canTradeMini =
    stock.exchange === 'FUTCOM' &&
    Number(userDetails?.FUTCOM_allow_mini) === 1 &&
    !!miniScripts[scriptKey] &&
    Number(miniScripts[scriptKey].mini_lot_size) > 0;
  const effectiveLotSize =
    lotVariant === 'MINI' && canTradeMini
      ? Number(miniScripts[scriptKey].mini_lot_size)
      : Number(stock.lotsize || stock.lotSize || 1) || 1;
  const orderLotVariant = (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA';
  const qtyRow = (!qtyRowsLoading && qtyRowsSegment === stock.exchange)
    ? pickQuantityTemplateRow(qtyRows, stock, orderLotVariant)
    : null;
  const openedPosition = openedLotsForScript(
    netPositions,
    stock,
    orderLotVariant,
    countAllVariantsForRow(qtyRows, stock, qtyRow)
  );

  const scriptWiseIntradayRate = () => {
    const raw = userDetails?.script_wise_margin?.[stock.scriptName];
    let data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return 0;
      }
    }
    if (!data || typeof data !== 'object') {
      return 0;
    }
    if (orderLotVariant === 'MINI'
        && data.mini_interaday !== undefined
        && data.mini_interaday !== null
        && String(data.mini_interaday) !== '') {
      const miniRate = Number(data.mini_interaday);
      if (Number.isFinite(miniRate)) {
        return miniRate;
      }
    }
    const megaRate = Number(data.interaday || 0);
    return Number.isFinite(megaRate) ? megaRate : 0;
  };

  const toWholeQuantity = (value) => {
    const next = Math.round(Number(value));
    return Number.isFinite(next) ? Math.max(0, next) : 0;
  };

  const toggleDepth = () => {
    setIsDepthExpanded(!isDepthExpanded);
  };

  const prevLotsRef = useRef(lots);
  const prevQuantityRef = useRef(quantity);

  useEffect(() => {
    if (isOpen) {
      const openAsMini = String(stock.lotVariant || '').toUpperCase() === 'MINI' && canTradeMini;
      setLotVariant(openAsMini ? 'MINI' : 'MEGA');
      setIsDepthExpanded(true);
      const miniSize = Number(miniScripts[scriptKey]?.mini_lot_size || 0);
      const initialLotSize = openAsMini && miniSize > 0
        ? miniSize
        : (stock.lotsize || stock.lotSize || 1);
      setQuantity(Math.max(toWholeQuantity(initialLotSize), 1));
      setLots(1);
      setLotsDraft(null);
      setQuantityDraft(null);
      prevLotsRef.current = 1;
      prevQuantityRef.current = Math.max(initialLotSize, 1);
    }
  }, [isOpen, stock.lotsize, stock.lotSize, stock.instrumentIdentifier, stock.lotVariant, canTradeMini]);

  useEffect(() => {
    if (!canTradeMini && lotVariant === 'MINI') {
      setLotVariant('MEGA');
    }
  }, [canTradeMini, lotVariant]);

  useEffect(() => {
    if (!isOpen || !stock.exchange) return undefined;
    let cancelled = false;
    const segment = stock.exchange;
    setQtyRowsLoading(true);
    apiAppFetch('getCustomQuantitySetting', {
      method: 'POST',
      body: JSON.stringify({ segment }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data && typeof data === 'object' ? Object.values(data) : []);
        setQtyRows(rows);
        setQtyRowsSegment(segment);
      })
      .catch(() => {
        if (cancelled) return;
        setQtyRows([]);
        setQtyRowsSegment(segment);
      })
      .finally(() => {
        if (!cancelled) setQtyRowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, stock.exchange]);

  useEffect(() => {
    const newQuantity = toWholeQuantity(Number(lots) * effectiveLotSize);
    if (prevLotsRef.current !== lots) {
      setQuantity(newQuantity);
      setQuantityDraft(null);
      prevLotsRef.current = lots;
      prevQuantityRef.current = newQuantity;
    }
  }, [lots, effectiveLotSize]);

  useEffect(() => {
    const newLots = Number((Number(quantity) / effectiveLotSize).toFixed(2));
    if (prevQuantityRef.current !== quantity) {
      setLots(newLots);
      prevQuantityRef.current = quantity;
      prevLotsRef.current = newLots;
    }
  }, [quantity, effectiveLotSize]);

  const isFutCom = stock.exchange === 'FUTCOM';
  const {
    lotStep,
    minLots,
    quantityStep,
  } = resolveFutComLotQtySteps({
    exchange: stock.exchange,
    scriptName: scriptKey,
    lotSize: effectiveLotSize,
    userDetails,
  });

  const lotsForOneQty = effectiveLotSize > 0 ? 1 / effectiveLotSize : 1;
  const lotsFloor = Math.max(minLots, lotsForOneQty);

  const applyLotsValue = (value) => {
    let next = Number(value);
    if (!Number.isFinite(next)) next = lotsFloor;
    next = Number(Math.max(lotsFloor, next).toFixed(2));
    let nextQty = toWholeQuantity(next * effectiveLotSize);
    if (nextQty < 1) {
      next = Number((Math.ceil(lotsForOneQty * 100) / 100).toFixed(2));
      nextQty = Math.max(1, toWholeQuantity(next * effectiveLotSize));
    }
    setLots(next);
    setQuantity(nextQty);
    prevLotsRef.current = next;
    prevQuantityRef.current = nextQty;
    setLotsDraft(null);
    setQuantityDraft(null);
  };

  const handleLotsChange = (e) => {
    let raw = String(e.target.value).replace(/[^\d.]/g, '');
    const dot = raw.indexOf('.');
    if (dot !== -1) {
      raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    }

    if (raw === '' || raw === '.') {
      setLotsDraft(raw);
      setQuantityDraft(null);
      setLots(0);
      setQuantity(0);
      prevLotsRef.current = 0;
      prevQuantityRef.current = 0;
      return;
    }

    const value = parseFloat(raw);
    if (!Number.isFinite(value)) {
      setLotsDraft(raw);
      return;
    }

    const nextQty = toWholeQuantity(value * effectiveLotSize);
    setLotsDraft(raw);
    setQuantityDraft(null);
    setLots(value);
    setQuantity(nextQty);
    prevLotsRef.current = value;
    prevQuantityRef.current = nextQty;
  };

  const commitLotsDraft = () => {
    if (lotsDraft === null) return;
    const value = parseFloat(lotsDraft);
    applyLotsValue(Number.isFinite(value) ? value : lotsFloor);
  };

  const incrementLots = () => {
    applyLotsValue(Number(lots) + lotStep);
  };

  const decrementLots = () => {
    if (Number(lots) > lotsFloor) {
      applyLotsValue(Number(lots) - lotStep);
    }
  };

  const applyQuantityValue = (value) => {
    let next = toWholeQuantity(value);
    if (next < 1) next = 1;
    const nextLots = Number((next / effectiveLotSize).toFixed(2));
    setQuantity(next);
    setLots(nextLots);
    prevQuantityRef.current = next;
    prevLotsRef.current = nextLots;
    setQuantityDraft(null);
    setLotsDraft(null);
  };

  const incrementQuantity = () => {
    applyQuantityValue(Number(quantity) + quantityStep);
  };

  const decrementQuantity = () => {
    applyQuantityValue(Number(quantity) - quantityStep);
  };

  const handleOrderTypeToggle = () => {
    setIsLimitOrder(!isLimitOrder);
    //alert("Limit Order: "+!isLimitOrder)
  };

  // Calculate the change percentage
  const _Bid = stock.buyPrice = stock.live.Bid!=undefined ? stock.live.Bid : stock.buyPrice;
  const _Ask = stock.sellPrice = stock.live.Ask!=undefined ? stock.live.Ask : stock.sellPrice;
  const _Open = stock.live.Open!=undefined ? stock.live.Open : stock.open;
  const _High = stock.live.High!=undefined ? stock.live.High : stock.high;
  const _Low = stock.live.Low!=undefined ? stock.live.Low : stock.low;
  const _Prev_Close = stock.live.Prev_Close!=undefined ? stock.live.Prev_Close : stock.close;
  const _LTP = stock.live.LTP!=undefined ? stock.live.LTP : stock.lastTradedPrice;

  const Change_per = ((_LTP - _Prev_Close) / _Prev_Close * 100).toFixed(2);
  const Change = (_LTP - _Prev_Close).toFixed(2);
  const changeColor = Change > 0 ? "green" : "red";

  const buyDepth = stock.live.depth?.buy || [];
  const sellDepth = stock.live.depth?.sell || [];

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  const handleOrderTypeChange = (event, newOrderType) => {
    setOrderType(newOrderType);
  };

  const handleQuantityChange = (event) => {
    const raw = String(event.target.value).replace(/[^\d]/g, '');
    setLotsDraft(null);

    if (raw === '') {
      setQuantityDraft('');
      setQuantity(0);
      setLots(0);
      prevQuantityRef.current = 0;
      prevLotsRef.current = 0;
      return;
    }

    const value = parseInt(raw, 10);
    if (!Number.isFinite(value)) {
      setQuantityDraft(raw);
      return;
    }

    const nextLots = Number((value / effectiveLotSize).toFixed(2));
    setQuantityDraft(raw);
    setQuantity(value);
    setLots(nextLots);
    prevQuantityRef.current = value;
    prevLotsRef.current = nextLots;
  };

  const commitQuantityDraft = () => {
    if (quantityDraft === null) return;
    const value = parseInt(quantityDraft, 10);
    applyQuantityValue(Number.isFinite(value) ? value : 1);
  };

  const createOrder_old = (orderType) => {
    const orderDetails = {
      stock,
      quantity,
      price: isLimitOrder === true ? price : (orderType === 'buy' ? _Ask : _Bid),
      orderType
    };
    console.log(orderDetails); // Replace this with your order creation logic
  };

  const watchlistLimitPriceError = (side) => {
    if (price == _LTP) {
      return 'Order price should not be equal to LTP.';
    }
    if (Number(price) < Number(stock.lower_circuit) || Number(price) > Number(stock.upper_circuit)) {
      return 'Order price is beyond daily price range.';
    }
    if (Number(side) === 1) {
      if (price >= stock.buyPrice) {
        return 'Order price must be less than bid price.';
      }
    } else if (price <= stock.sellPrice) {
      return 'Order price should be greater than ask price.';
    }
    return '';
  };

  const placeWatchlistNetOrder = async (orderType, position, plan) => {
    setDisable(true);
    if (plan.rejectLimitOverQty) {
      alert('For a limit order, quantity cannot be more than the open quantity.');
      setDisable(false);
      return;
    }
    if (Number(quantity) < 1) {
      alert('Quantity should be at least 1.');
      setDisable(false);
      return;
    }
    if (isLimitOrder) {
      if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
        alert('Price not updated, please refresh');
        setDisable(false);
        return;
      }
      const limitError = watchlistLimitPriceError(orderType);
      if (limitError) {
        alert(limitError);
        setDisable(false);
        submitRejectionLog(JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"' + stock.dispname + '"',
          stockExpiry: '"' + stock.expiry + '"',
          orderTransactionType: 2,
          orderExecutionType: 2,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"' + limitError + '"',
          orderPrice: price,
          lotVariant: orderLotVariant,
        }));
        return;
      }
    }

    const squareOff = Number(userDetails.onlyPositionSquareOff) === 1
      || Number(userDetails.masterOnlyPositionSquareOff) === 1;
    const extraQty = squareOff ? 0 : Number(plan.extraQty);
    const closeQty = Number(plan.closeQty);
    const lotSize = Number(position.usedLotSize || position.lotSize || position.stockLotSize || effectiveLotSize) || effectiveLotSize;
    const entryPrice = Number(orderType) === 1 ? Number(position.avgSellPrice || 0) : Number(position.avgBuyPrice || 0);
    const closeStock = {
      ...stock,
      dispName: stock.dispname,
      stockId: position.stockId,
      stockName: position.stockName || stock.dispname,
      instrumentIdentifier: position.stockId || stock.instrumentIdentifier,
      exchange: position.exchange || stock.exchange,
      expiry: position.expiry || stock.expiry,
      usedLotSize: lotSize,
      stockLotSize: lotSize,
      lotSize: lotSize,
      lotsize: lotSize,
      avgBuyPrice: position.avgBuyPrice,
      avgSellPrice: position.avgSellPrice,
      positionId: position.positionId,
      autoCutDate: position.autoCutDate || stock.autoCutDate,
      lotVariant: orderLotVariant,
    };

    try {
      const closeResponse = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock: closeStock,
          orderTypeBuySell: orderType,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lotSize > 0 ? closeQty / lotSize : closeQty,
          quantity: closeQty,
          lotVariant: orderLotVariant,
          orderTransactionType: 2,
          market_type: stock.exchange + '_intraday',
          limitSlPrice: price,
          entryPrice: entryPrice,
          orderDevice: 1,
          ip: process.env.REACT_APP_USER_IP,
        }),
      });
      const closeData = await closeResponse.json();
      if (!closeData.success) {
        setDisable(false);
        alert(closeData.message || 'Close order failed.');
        return;
      }
      const cancelNote = pendingOrdersCancelledNote(closeData.message);

      if (extraQty > 0.0001) {
        const openStock = { ...stock, dispName: stock.dispname };
        const openResponse = await mediatorAppFetch({
          method: 'POST',
          body: JSON.stringify({
            stock: openStock,
            orderTypeBuySell: orderType,
            orderExecutionType: isLimitOrder ? 2 : 1,
            lots: effectiveLotSize > 0 ? extraQty / effectiveLotSize : extraQty,
            lotVariant: orderLotVariant,
            orderTransactionType: 1,
            market_type: stock.exchange + '_intraday',
            limitSlPrice: price,
            orderDevice: 1,
            ip: process.env.REACT_APP_USER_IP,
          }),
        });
        const openData = await openResponse.json();
        setDisable(false);
        refreshTradesAndPortfolio('both');
        if (!openData.success) {
          alert('Position closed. The extra quantity was not opened. ' + (openData.message || '') + cancelNote);
          return;
        }
        const sideLabel = Number(orderType) === 1 ? 'Buy' : 'Sell';
        setSuccessAlert({
          show: true,
          title: 'Order Successful',
          message: sideLabel + ' closed ' + closeQty + ' and opened ' + extraQty + ' of ' + stock.dispname + '.' + cancelNote,
        });
        return;
      }

      setLots(1);
      setLotsDraft(null);
      setQuantityDraft(null);
      setDisable(false);
      refreshTradesAndPortfolio('both');
      const sideLabel = Number(orderType) === 1 ? 'Buy' : 'Sell';
      const closedNote = squareOff && Number(plan.extraQty) > 0.0001
        ? ' Square-off only, so the quantity above the open position was not opened.'
        : '';
      setSuccessAlert({
        show: true,
        title: isLimitOrder ? 'Limit Order Successful' : 'Order Successful',
        message: sideLabel + ' ' + closeQty + ' of ' + stock.dispname + '.' + closedNote + cancelNote,
      });
    } catch (error) {
      console.error(error);
      setDisable(false);
      alert('Order failed. Please try again.');
    }
  };

  const createOrder = async (orderType) => {
    const position = findWatchlistPosition(stock, netPositions, orderLotVariant);
    const plan = planOppositeWatchlistOrder(position, orderType, quantity, isLimitOrder);
    if (plan) {
      return placeWatchlistNetOrder(orderType, position, plan);
    }

    setDisable(true);

    if (userDetails.marginAvailable <= 0) {
      alert("You don't have sufficient margin.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have sufficient margin."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);

      return;
    }

    if (quantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Quantity should be at least 1."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);

      return;
    }

    // if(stock.exchange === "OPTSTK"){
    //   if (typeof lots === 'number' && lots % 1 !== 0) {
    //     // console.log("The value is in points (decimal).");

    //     alert("Lots should not be in points.");
    //     setDisable(false);

    //     var data = JSON.stringify({
    //       clientId: userDetails.clientId,
    //       parent_id: userDetails.parentId,
    //       stockId: stock.instrumentIdentifier,
    //       stockName: '"'+stock.dispname+'"',
    //       stockExpiry: '"'+stock.expiry+'"',
    //       orderTransactionType: 1,
    //       orderExecutionType: isLimitOrder ? 2 : 1,
    //       buy_sell: orderType,
    //       lot: lots,
    //       quantity: quantity,
    //       remark: '"Lots should not be in points."',
    //       orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
    //     });

    //     submitRejectionLog(data);

    //     return;

    //   }
    // }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      if (Number(userDetails.availableMargin) < Number(userDetails.OPTSTK_balance)) {
        alert(`You don't have sufficient margin to sell.\nMinimum ${userDetails.OPTSTK_balance} is required.`);
        setDisable(false);

        var data = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"You don not have sufficient margin to sell."',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
          lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
        });
  
        submitRejectionLog(data);

        return;
      }
    }

    // const nowTime = new Date();
    // const lastTradeTimeUnix = new Date(stock.lastTradeTime * 1000);
    // const timeDiffInSecond = Math.ceil((nowTime - lastTradeTimeUnix) / 1000);

    // if (stock.autoCutDate <= new Date().toISOString().split('T')[0]) {
    //   alert("You can't trade on autocut date.\nOpened position can be closed from portfolio.");
    //   var data = JSON.stringify({
    //     clientId: userDetails.clientId,
    //     parent_id: userDetails.parentId,
    //     stockId: stock.instrumentIdentifier,
    //     stockName: '"'+stock.dispname+'"',
    //     stockExpiry: '"'+stock.expiry+'"',
    //     orderTransactionType: 1,
    //     orderExecutionType: isLimitOrder ? 2 : 1,
    //     buy_sell: orderType,
    //     lot: lots,
    //     quantity: quantity,
    //     remark: '"You can not trade on autocut date.\nOpened position can be closed from portfolio."',
    //     orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
    //   });

    //   submitRejectionLog(data);
    //   return;
    // }

    if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
      alert("Price not updated, please refresh");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Price not updated, please refresh."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);
      
      return;
    }

    if (userDetails.availableMargin <= 0) {
      alert("You do not have sufficient margin.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have sufficient margin."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);
      return;
    }

    let orderAmount = 0;

    if (isLimitOrder) {

      if(price == _LTP){
          alert("Order price should not be equal to LTP.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should not be equal to LTP."',
            orderPrice: price,
            lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
          });
    
          submitRejectionLog(data);

          return;
      }

      orderAmount = quantity * price;

      if(Number(price) < Number(stock.lower_circuit) || Number(price) > Number(stock.upper_circuit)) {
          alert("Order price is beyond daily price range.");
          
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price is beyond daily price range"',
            orderPrice: price,
            lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
          });

          submitRejectionLog(data);

          return;
      }

      if (orderType === 1) {
        if (price >= stock.buyPrice) {
          alert("Order price must be less than bid price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price must be less than bid price"',
            orderPrice: price,
            lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
          });

          submitRejectionLog(data);

          return;
        }
      } else {
        if (price <= stock.sellPrice) {
          alert("Order price should be greater than ask price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should be greater than ask price"',
            orderPrice: price,
            lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
          });

          submitRejectionLog(data);

          return;
        }
      }
    }

    if (orderType === 2) 
    {
        if (stock.buyPrice <= 0) 
        {
            alert("Price not updated, please refresh");
            setDisable(false);

            var data = JSON.stringify({
              clientId: userDetails.clientId,
              parent_id: userDetails.parentId,
              stockId: stock.instrumentIdentifier,
              stockName: '"'+stock.dispname+'"',
              stockExpiry: '"'+stock.expiry+'"',
              orderTransactionType: 1,
              orderExecutionType: isLimitOrder ? 2 : 1,
              buy_sell: orderType,
              lot: lots,
              quantity: quantity,
              remark: '"Price not updated, please refresh."',
              orderPrice: stock.buyPrice,
              lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
            });
  
            submitRejectionLog(data);

            return;
        }
        orderAmount = quantity * stock.buyPrice;
    } 
    else if (orderType === 1) {
      if (stock.sellPrice <= 0) {
          alert("Price not updated, please refresh");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: isLimitOrder ? 2 : 1,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Price not updated, please refresh."',
            orderPrice: stock.sellPrice,
            lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
          });

          submitRejectionLog(data);

          return;
      }
      orderAmount = quantity * stock.sellPrice;
    }

    let requiredMargin = 0;

    if (stock.exchange === "FUTCOM") {
      if(userDetails.FUTCOM_margin_type === "script_wise")
      {
        console.log(stock.scriptName);
        console.log(userDetails);

        // Parse the script wise margin data and get interday value
        const scriptInterdayValue = scriptWiseIntradayRate();
        console.log("Script interday value:", scriptInterdayValue);
        
        // Calculate required margin using script wise interday value
        requiredMargin = scriptInterdayValue;
        // return;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTCOM_intraday;
      }
    } else if (stock.exchange === "FUTSTK") {
      const futstkScripts = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
      if(userDetails.FUTSTK_margin_type === "script_wise" && futstkScripts.includes(stock.scriptName))
      {
        const scriptInterdayValue = scriptWiseIntradayRate();
        requiredMargin = scriptInterdayValue;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTSTK_intraday;
      }
    } else if (stock.exchange === "OPTSTK") {
      requiredMargin = orderAmount / userDetails.OPTSTK_intraday;
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      requiredMargin = lots * userDetails.OPTSTK_balance;
    }

    if (requiredMargin > userDetails.availableMargin) {
      alert(`Can't place order.\nRequired margin: ${Number(requiredMargin).toFixed(2)}, current margin: ${Number(userDetails.availableMargin).toFixed(2)}`);
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Can not place order. Required margin: '+Number(requiredMargin).toFixed(2)+', current margin: '+Number(userDetails.availableMargin).toFixed(2)+'"',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);

      return;
    }

    if (lots <= 0 || lots <= 0.0) {
      alert("Please enter lot quantity.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Please enter lot quantity."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
        lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
      });

      submitRejectionLog(data);

      return;
    }

    stock.dispName = stock.dispname;

    try {
      const response = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock,
          orderTypeBuySell: orderType,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lots,
          lotVariant: canTradeMini && lotVariant === 'MINI' ? 'MINI' : 'MEGA',
          orderTransactionType: 1,
          market_type: `${stock.exchange}_intraday`,
          limitSlPrice: price,
          orderDevice: 1,
          ip: process.env.REACT_APP_USER_IP
        }),
      });
      const data = await response.json();

      if (!data.success) {
        
        setDisable(false);
        
        var msg = data.message;
        
        if (data.message === "open position") {
          msg = "Please close position from portfolio.";
        } else if (data.message === "open pending position") {
          msg = "You have a pending limit order on this script. You can cancel it from Trades."
        } else if (data.message.includes("Blocked")) {
          msg = "Trading not allowed on this script.";
        } else {
          msg = data.message;
          //alert(data.message);
        }


        showToast(3, msg, "Order Failed");
        
        var dataa = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"'+msg+'"',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice),
          lotVariant: (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA'
        });
  
        submitRejectionLog(dataa);
      } 
      else 
      {
        setLots(1);
        setLotsDraft(null);
        setQuantityDraft(null);
        setDisable(false);

        var b_s = orderType == 1 ? 'Buy' : 'Sell';
        var msg = b_s + " " + Number(lots).toFixed(2) + " Lot(s) of " + stock.dispName + " @" + Number(data.orderPrice).toFixed(2);
        refreshTradesAndPortfolio('both');
        // Custom success alert (works on all devices including iPhone); sheet closes only after user taps OK
        setSuccessAlert({
          show: true,
          title: isLimitOrder ? 'Limit Order Successful' : 'Order Successful',
          message: msg
        });

      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  return (
    <>
      <ToastContainer />
      {/* Custom success alert overlay - like SweetAlert, works on iOS */}
      {successAlert.show && (
        <animated.div
          className={`success-alert-overlay ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          style={{ opacity: successAlertSpring.opacity }}
          data-theme={isDarkMode ? 'dark' : 'light'}
          onClick={e => e.stopPropagation()}
        >
          <animated.div
            className="success-alert-box"
            style={{
              transform: successAlertSpring.scale.to(s => `scale(${s})`),
              opacity: successAlertSpring.opacity
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="success-alert-icon-wrap">
              <CheckCircleIcon className="success-alert-icon" />
            </div>
            <Typography variant="h6" className="success-alert-title">{successAlert.title}</Typography>
            <Typography className="success-alert-message">{successAlert.message}</Typography>
            <Button
              variant="contained"
              className="success-alert-btn"
              onClick={handleSuccessAlertClose}
              fullWidth
            >
              OK
            </Button>
          </animated.div>
        </animated.div>
      )}
      {isOpen && <div className="backdrop" onClick={handleBackdropClick} />}
      {isOpen && <animated.div style={{ styles }} className={`bottom-sheet ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
        <Box className="bottom-sheet-header">
          <Typography variant="h6" className='bottom-sheet-scriptName'>
            <p>
                <span style={{fontWeight:'600'}}>
                  {stock.dispname}
                  {lotVariant === 'MINI' && <span className="lot-variant-mini">MINI</span>}
                </span> <span className='stock-expiry'>{stock.expiry}</span>
            </p>
            <p className='bottom-sheet-exchange'>{stock.exchange}</p>
          </Typography>
        
          {/* <IconButton onClick={onClose} className='bottomSheet-close-btn'>
            <CloseIcon />
          </IconButton> */}
          <p style={{fontSize:'14px', fontWeight:'500'}}>
            <span style={{ position:' absolute', right: '20px', fontWeight: '600'}}>
              ₹{Number(_LTP).toFixed(2)}
              {Change > 0 ? (
                  <img src={upIcon} alt="Up" className="change-icon" />
              ) : (
                  <img src={downIcon} alt="Down" className="change-icon" />
              )}
            </span> <br/> 
            <span className={changeColor}>{Change}({Change_per}{'%'})</span>
          </p>
        </Box>
        <Box className="bottom-sheet-content">
          {qtyRowsSegment === stock.exchange && !qtyRowsLoading && (
            <div className="stock-details qty-limit-strip">
              {qtyRow ? (
                <>
                  <span className="qty-limit-item">
                    <span className="qty-limit-label">Opened</span>
                    <span className="qty-limit-value">{formatLotCount(openedPosition.quantity)} ({formatLotCount(openedPosition.lots)})</span>
                  </span>
                  <span className="qty-limit-item">
                    <span className="qty-limit-label">Positional</span>
                    <span className="qty-limit-value">{formatLotCount(qtyRow.positional_lot)}</span>
                  </span>
                  <span className="qty-limit-item">
                    <span className="qty-limit-label">Max order</span>
                    <span className="qty-limit-value">{formatLotCount(qtyRow.max_order)}</span>
                  </span>
                </>
              ) : (
                <span className="qty-limit-label">Quantity not allotted for this script.</span>
              )}
            </div>
          )}
          <Grid container spacing={2} className="stock-details">
            <Grid item xs={3} className='center'>
              <Typography>Open</Typography>
              <Typography className='ohlc'>{Number(_Open).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={3} className='center'>
              <Typography>High</Typography>
              <Typography className='ohlc'>{Number(_High).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={3} className='center'>
              <Typography>Low</Typography>
              <Typography className='ohlc'>{Number(_Low).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={3} className='center'>
              <Typography>Close</Typography>
              <Typography className='ohlc'>{Number(_Prev_Close).toFixed(2)}</Typography>
            </Grid>
          </Grid>
          {stock.exchange!='OPTSTK' && <div className="stock-details dpr" style={{marginTop:'3px !important'}}>
            <p style={{textAlign:'center', fontWeight:'600', fontSize:'12px'}}>Day Price Range: { stock.lower_circuit } - { stock.upper_circuit }</p>
          </div>}
          {buyDepth.length > 0 && (
            <>
              <Typography variant="h6" className='market-depth-title' onClick={toggleDepth} style={{ cursor: 'pointer', display: 'flex', justifyContent:'space-between' }}>
                 {/* <span>Market Depth {isDepthExpanded ? '-' : '+'}</span> */}
                 { userDetails.showMarketDepth === 1 && <span style={{fontWeight:600}}>Market Depth</span>}
                 <span style={{fontWeight:600}}>Balance: {Number(userDetails.availableMargin).toFixed(2)}</span>
              </Typography>
              {isDepthExpanded && userDetails.showMarketDepth === 1 && (
                <div className='market-depth'>
                  <Box className="order-depth">
                    <TableContainer component={Paper} className='noborder' style={{ backgroundColor: 'inherit' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell className='left-align padding5'>Qty</TableCell>
                            <TableCell className='right-align padding5'>Buy Price</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {buyDepth.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className='left-align padding5 noborder'>{row.quantity}</TableCell>
                              <TableCell className='green right-align padding5 noborder'>{Number(row.price).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Box className="order-depth">
                    <TableContainer component={Paper} className='noborder' style={{ backgroundColor: 'inherit' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell className='left-align padding5'>Sell Price</TableCell>
                            <TableCell className='right-align padding5'>Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sellDepth.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className='red left-align padding5 noborder'>{Number(row.price).toFixed(2)}</TableCell>
                              <TableCell className='right-align padding5 noborder'>{row.quantity}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </div>
              )}
            </>
          )}

          <Box className="lot-quantity-section">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} className='center'>
                <Typography>Lots</Typography>
                <div style={{textAlign:'center'}}>
                  <Button className='plus-minus_btn' onClick={decrementLots}>-</Button>
                  {/* {stock.exchange == "FUTSTK" && <input value={Number(lots).toFixed(2)} variant="outlined" size="small" step="1.0" disabled  className='input-text-field'/>}
                  {stock.exchange != "FUTSTK" && <input value={Number(lots).toFixed(2)} variant="outlined" size="small" min="0.1" step="0.1" disabled  className='input-text-field'/>} */}
                  <input
                    inputMode="decimal"
                    value={lotsDraft !== null ? lotsDraft : Number(lots).toFixed(2)}
                    onChange={handleLotsChange}
                    onBlur={commitLotsDraft}
                    min={minLots}
                    step={lotStep}
                    className='input-text-field'
                  />
                  <Button className='plus-minus_btn' onClick={incrementLots}>+</Button>
                </div>
              </Grid>
              <Grid item xs={6} className='center'>
                <Typography>Quantity</Typography>
                <div style={{textAlign:'center'}}>
                  <Button className='plus-minus_btn' onClick={decrementQuantity}>-</Button>
                  {/* {stock.exchange == "FUTSTK" && <input value={Number(quantity).toFixed(2)} variant="outlined" size="small" min="1" step="1" onChange={handleQuantityChange} className='input-text-field'/>}
                  {stock.exchange != "FUTSTK" && <input value={Number(quantity).toFixed(2)} variant="outlined" size="small" min="1" step="0.1" disabled  onChange={handleQuantityChange} className='input-text-field'/>} */}
                  
                  
                  {/* {stock.exchange === "OPTSTK" && <input value={Number(quantity)} variant="outlined" size="small" min="1" step="0.1" disabled onChange={handleQuantityChange} className='input-text-field'/>} */}
                  <input
                    inputMode="numeric"
                    value={quantityDraft !== null ? quantityDraft : toWholeQuantity(quantity)}
                    variant="outlined"
                    size="small"
                    min={1}
                    step={quantityStep}
                    onChange={handleQuantityChange}
                    onBlur={commitQuantityDraft}
                    className='input-text-field'
                  />
                  
                  
                  <Button className='plus-minus_btn' onClick={incrementQuantity}>+</Button>
                </div>
              </Grid>
            </Grid>
          </Box>

            {stock.exchange!='OPTSTK' && <Box className="order-type-section" style={{display:'flex'}}>
                
                <div style={{ textAlign:'center', margin:'3px auto'}}>
                    <div className="toggle-switch" style={{width:'139px'}}>
                      <span className={!isLimitOrder ? 'active' : ''} onClick={handleOrderTypeToggle}>Market</span>
                      <span className={isLimitOrder ? 'active' : ''} onClick={handleOrderTypeToggle}>Limit</span>
                    </div>
                </div>
              
              {isLimitOrder && (
                <div style={{ textAlign:'center'}}>
                  {/* <Button className='plus-minus_btn' onClick={decrementQuantity}>-</Button> */}
                      <input type="number"
                        label="Price"
                        variant="outlined"
                        size="small"
                        value={price}
                        placeholder='Price'
                        className='input-text-field limit-price-box'
                        onChange={(e) => setPrice(e.target.value)}
                      />
                  {/* <Button className='plus-minus_btn' onClick={incrementQuantity}>+</Button> */}
                </div>
                
                    
              )}
              
            </Box>}
          
          
          
            <Box className="bottom-actions">
            
                {/* {(userDetails.OPTSTK_sellAllow === "1" && stock.exchange == "OPTSTK") || (stock.exchange != "OPTSTK") && <Button variant="contained" color="secondary" disabled={disableBtn} className='button-sell' onClick={() => createOrder(2)} >
                  Sell {userDetails.OPTSTK_sellAllow} {stock.exchange}
                </Button>} */}
                {(userDetails.OPTSTK_sellAllow == 1 && stock.exchange === "OPTSTK") || stock.exchange !== "OPTSTK" ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={disableBtn}
                    className='button-sell'
                    onClick={() => createOrder(2)}
                  >
                    Sell
                  </Button>
                ) : null}
                  
                <Button variant="contained" color="primary" disabled={disableBtn} className='button-buy' onClick={() => createOrder(1)} >
                  Buy
                </Button>
            </Box>
            {buyDepth.length > 0 && <Box className="bottom-actions" style={{ marginTop:'3px', fontSize: '11px', color: 'gray' }}>
                  {/* <p>
                    Intraday Margin: 
											{ stock.exchange=='OPTSTK' ? userDetails.OPTSTK_balance * lots : ((quantity*buyDepth[0]['price'])/stock.intraday).toFixed(2) }
                  </p> */}
                  {(userDetails.OPTSTK_sellAllow == 1 && stock.exchange === "OPTSTK") || stock.exchange !== "OPTSTK" ? (
                    <p>
                    Intraday Margin: 
											{ stock.exchange=='OPTSTK' ? userDetails.OPTSTK_balance * lots : 
                        (stock.exchange === 'FUTCOM' && userDetails.FUTCOM_margin_type === 'script_wise') || 
                        (stock.exchange === 'FUTSTK' && userDetails.FUTCOM_margin_type === 'script_wise' && ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'].includes(stock.scriptName)) ? 
                          (lots * scriptWiseIntradayRate()).toFixed(2) : 
                          ((quantity*buyDepth[0]['price'])/stock.intraday).toFixed(2) }
                  </p>
                ) : null}
                  <p> Intraday Margin: { 
                    (stock.exchange === 'FUTCOM' && userDetails.FUTCOM_margin_type === 'script_wise') || 
                    (stock.exchange === 'FUTSTK' && userDetails.FUTCOM_margin_type === 'script_wise' && ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'].includes(stock.scriptName)) ? 
                      (lots * scriptWiseIntradayRate()).toFixed(2) : 
                      ((quantity*sellDepth[0]['price'])/stock.intraday).toFixed(2) 
                  }  </p>
            </Box> }
        </Box>
      </animated.div>}
    </>
  );
};

export default BottomSheet;
